import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { listDocumentMeta } from '../lib/docMeta';
import { useStore } from '../store/useStore';
import { useDocControllerContext } from '../hooks/DocControllerContext';
import { useT } from '../i18n/useT';
import { Icon } from './Icon';
import { Menu, MenuItem, MenuSeparator, anchorAtPointer } from './Menu';
import type { MenuAnchor } from './Menu';

/**
 * Open-document tabs. Shows only when more than one document is open, so a
 * single-doc workflow stays chrome-free. The active tab mirrors `activeDoc.id`.
 */
export function TabBar() {
  const t = useT();
  const controller = useDocControllerContext();
  const openTabs = useStore((s) => s.openTabs);
  const activeId = useStore((s) => s.activeDoc.id);
  const closeTab = useStore((s) => s.closeTab);
  const docs = useLiveQuery(() => listDocumentMeta(), [], []);
  const [menu, setMenu] = useState<{ id: string; anchor: MenuAnchor } | null>(null);

  const titleOf = (id: string) => docs.find((d) => d.id === id)?.title || t('common.untitled');
  const tabs = openTabs.filter((id) => docs.some((d) => d.id === id));
  if (tabs.length <= 1) return null;

  const close = (id: string) => {
    const next = closeTab(id);
    if (next) void controller.open(next);
  };
  const closeOthers = (keep: string) => {
    for (const id of tabs) if (id !== keep) closeTab(id);
    void controller.open(keep);
  };

  return (
    <div className="mb-tabbar" role="tablist">
      {tabs.map((id) => {
        const selected = id === activeId;
        return (
          // The label button is the tab: `role="tab"` must not wrap focusable
          // children, and it owns the roving tabindex. This wrapper only groups
          // the label with its close control, so it stays presentational —
          // a tablist's semantic children have to be the tabs themselves.
          <div key={id} role="presentation" className={`mb-tab ${selected ? 'active' : ''}`}>
            <button
              className="mb-tab-label"
              role="tab"
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => void controller.open(id)}
              // Middle-click closes, matching every tabbed UI.
              onAuxClick={(e) => {
                if (e.button === 1) {
                  e.preventDefault();
                  close(id);
                }
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                setMenu({ id, anchor: anchorAtPointer(e) });
              }}
              title={titleOf(id)}
            >
              {titleOf(id)}
            </button>
            <button
              className="mb-tab-close"
              aria-label={t('tab.close')}
              title={t('tab.close')}
              tabIndex={-1}
              onClick={() => close(id)}
            >
              <Icon name="x" size={13} />
            </button>
          </div>
        );
      })}

      {menu && (
        <Menu anchor={menu.anchor} onClose={() => setMenu(null)} label={titleOf(menu.id)}>
          <MenuItem onSelect={() => close(menu.id)}>
            <Icon name="x" size={15} />
            {t('tab.close')}
          </MenuItem>
          <MenuItem disabled={tabs.length < 2} onSelect={() => closeOthers(menu.id)}>
            {t('tab.closeOthers')}
          </MenuItem>
          <MenuSeparator />
          <MenuItem onSelect={() => useStore.getState().setTagsEditorFor(menu.id)}>
            <Icon name="tag" size={15} />
            {t('tags.edit')}
          </MenuItem>
          <MenuItem onSelect={() => useStore.getState().setMoveToFolderFor(menu.id)}>
            <Icon name="folder-input" size={15} />
            {t('folder.move')}
          </MenuItem>
        </Menu>
      )}
    </div>
  );
}
